import { FIRM } from "@/lib/firm";

export type BusinessLocation = {
  slug: string;
  name: string;
  title: string;
  metaTitle: string;
  description: string;
  lead: string;
  paragraphs: string[];
  localNotes: { t: string; d: string }[];
  sectors: string[];
  nearby: string[];
  faqs: { q: string; a: string }[];
};

const phone = FIRM.phone;

export const BUSINESS_LOCATIONS: BusinessLocation[] = [
  {
    slug: "golden-beach",
    name: "Golden Beach",
    title: "Business conveyancing and business sales in Golden Beach",
    metaTitle: `Golden Beach Business Lawyers & Business Sales | ${FIRM.name}`,
    description: `Business sale and purchase lawyers in Golden Beach. Fixed-fee contract review, lease assignment, due diligence and commercial conveyancing. Call ${phone}.`,
    lead: "Our office is on Theresa Street, Golden Beach. If you are buying or selling a business along the Esplanade or anywhere around the Passage, we can meet you at the shop rather than the other way round.",
    paragraphs: [
      "Golden Beach trade is seasonal and largely owner-operated: cafes and takeaways along the Esplanade, holiday accommodation, marine and boating services off the Passage, hairdressers and allied health in the local strips. Most sales here are asset sales of a small owner-run business with a shop lease, some plant and a handful of staff.",
      "The deal usually turns on three things — whether the landlord will assign the lease on terms the buyer can live with, whether the financials stand up when you reconcile them against the bank statements, and whether the fit-out and equipment are actually owned rather than financed.",
      "We review the contract before you sign, run the searches, negotiate the lease assignment with the landlord's agent and get you to settlement on a fixed fee quoted up front.",
    ],
    localNotes: [
      {
        t: "Esplanade retail leases",
        d: "Short remaining terms and unexercised options are the most common problem in Golden Beach shop sales. A buyer paying for goodwill needs the term — or the option — locked in before the contract goes unconditional.",
      },
      {
        t: "Seasonal trading figures",
        d: "Passage-front hospitality swings hard between school holidays and the shoulder season. We check the trading period the figures cover and press for enough months to see the full cycle.",
      },
      {
        t: "Marine and boating operators",
        d: "Pontoon, ramp and foreshore approvals do not always transfer with the business. We confirm what permits exist and whether council or the state has to consent.",
      },
      {
        t: "Home-based and holiday letting",
        d: "Where the business operates from a dwelling or short-stay property, the council approval and any body corporate by-laws matter as much as the contract.",
      },
    ],
    sectors: ["Cafes and takeaway", "Holiday accommodation", "Marine services", "Allied health", "Hair and beauty"],
    nearby: ["Caloundra", "Pelican Waters", "Kings Beach", "Aroona", "Little Mountain"],
    faqs: [
      {
        q: "Do you review business sale contracts in Golden Beach?",
        a: `Yes. Send us the contract before you sign and we will review it, tell you what to change, and quote a fixed fee to act. Our office is at ${FIRM.street}, Golden Beach and we are happy to meet at your premises.`,
      },
      {
        q: "How long does a small business sale take to settle here?",
        a: "Typically six to ten weeks. The critical path is almost always landlord consent to the lease assignment, so we start that on day one rather than waiting for due diligence to finish.",
      },
      {
        q: "Do you also act on the commercial property?",
        a: "Yes. Where the buyer is taking the premises as well as the business, we run the commercial conveyance and the business sale as one matter.",
      },
    ],
  },
  {
    slug: "caloundra",
    name: "Caloundra",
    title: "Caloundra business sale, purchase and commercial conveyancing lawyers",
    metaTitle: `Caloundra Business Sale & Commercial Conveyancing Lawyers | ${FIRM.name}`,
    description: `Caloundra business lawyers for buying and selling a business: contract review, due diligence, lease assignment, licences and settlement. Fixed fees. Call ${phone}.`,
    lead: "Caloundra is the commercial heart of the southern Sunshine Coast — Bulcock Street retail, the CBD professional suites, and the Caloundra West and Corporate Park industrial estates. We act for buyers and sellers across all of it.",
    paragraphs: [
      "Business sales in Caloundra run the full range: a Bulcock Street cafe changing hands for six figures, a trade business with vehicles and a Caloundra West shed, a professional practice with a client list and staff, and management rights over a Kings Beach or Moffat Beach complex.",
      "Bigger deals bring more moving parts — restraint of trade clauses that have to be enforceable, employee entitlements that have to be adjusted at settlement, PPSR registrations over financed plant, and licences that do not transfer automatically.",
      "We handle the contract, due diligence coordination, lease assignment, PPSR searches and releases, and settlement, and we tell you the fee before we start.",
    ],
    localNotes: [
      {
        t: "Bulcock Street retail leases",
        d: "Retail shop leases are regulated in Queensland: disclosure statements, a legal and financial advice certificate and the landlord's consent process all have to be handled properly or the assignment stalls.",
      },
      {
        t: "Caloundra West and industrial estates",
        d: "Trade and light industrial businesses come with vehicles, equipment finance and a warehouse lease. PPSR searches and payout figures are where these deals go wrong.",
      },
      {
        t: "Licensed premises",
        d: "Liquor, food and gaming approvals need their own transfer applications on their own timeline. Making settlement conditional on the licence is safer than hoping it lands in time.",
      },
      {
        t: "Management rights",
        d: "Caloundra has a deep pool of accommodation complexes. Management rights sales need body corporate approval, an assignment of the caretaking and letting agreements and a verified profit figure.",
      },
    ],
    sectors: ["Retail and hospitality", "Trade and light industrial", "Professional practices", "Management rights", "Health and fitness"],
    nearby: ["Golden Beach", "Kings Beach", "Moffat Beach", "Dicky Beach", "Currimundi", "Little Mountain"],
    faqs: [
      {
        q: "What does it cost to have a business contract reviewed in Caloundra?",
        a: `We quote a fixed fee once we have seen the contract and know whether a lease assignment, licence transfer or franchise consent is involved. The first conversation is obligation free — call ${phone}.`,
      },
      {
        q: "Do I need a lawyer to buy a small business?",
        a: "Business contracts are not standardised the way residential conveyancing is. The risk sits in the lease, the warranties, the restraint and the settlement mechanics, and those are the clauses that get amended. Have it reviewed before you sign, or keep it conditional so it can be.",
      },
      {
        q: "Do you act on retail shop lease assignments?",
        a: "Yes — for outgoing tenants, incoming tenants and landlords, including the disclosure statement and advice certificate requirements under the Retail Shop Leases Act.",
      },
    ],
  },
  {
    slug: "pelican-waters",
    name: "Pelican Waters",
    title: "Pelican Waters business and commercial property lawyers",
    metaTitle: `Pelican Waters Business & Commercial Property Lawyers | ${FIRM.name}`,
    description: `Business sale, purchase and commercial lease advice for Pelican Waters operators. Fixed-fee contract review, due diligence and settlement. Call ${phone}.`,
    lead: "Pelican Waters keeps growing — the town centre, the marina precinct and the golf resort have brought a wave of new tenancies and new operators. We act on the sales, the leases and the exits.",
    paragraphs: [
      "A lot of Pelican Waters business work is first-generation: a new tenancy in the town centre, a fit-out to negotiate, an incentive to document, and a landlord's standard lease that heavily favours the landlord. Get that lease right and the business is saleable in five years; get it wrong and no buyer will pay for goodwill.",
      "The other half is the exit — selling a service business with a strong local client base, or a hospitality tenancy where the value is the lease and the fit-out rather than the plant.",
      "We review and negotiate new leases, act on business sales and purchases, and handle the commercial conveyancing where the premises are being bought outright.",
    ],
    localNotes: [
      {
        t: "New town centre tenancies",
        d: "Fit-out contributions, rent-free periods and make-good obligations are the clauses that decide whether a new tenancy is worth taking. They must be documented in the lease, not agreed by email.",
      },
      {
        t: "Marina and resort precinct",
        d: "Operators in the marina and golf precinct often deal with layered approvals and precinct rules on top of the lease. We check what binds the business before you commit.",
      },
      {
        t: "Make-good exposure",
        d: "A make-good clause on a heavily fitted-out tenancy can be a five-figure liability at the end of the term. It is negotiable at the start and rarely negotiable at the end.",
      },
      {
        t: "Owner-occupier purchases",
        d: "Where an operator buys the premises, GST, the going-concern position and any existing tenancies all have to be handled in the contract.",
      },
    ],
    sectors: ["Town centre retail", "Hospitality", "Professional services", "Health and wellness", "Marine and tourism"],
    nearby: ["Golden Beach", "Caloundra", "Aroona", "Little Mountain", "Baringa"],
    faqs: [
      {
        q: "Can you review a commercial lease before I sign it?",
        a: "Yes, and it is the cheapest legal work you will ever buy. We mark up the lease, explain the commercial effect of each change and deal with the landlord's solicitor.",
      },
      {
        q: "Do you act for buyers of Pelican Waters businesses?",
        a: "Yes. We review the contract, coordinate the due diligence requests, run the PPSR and licence checks and take the deal through to settlement on a fixed fee.",
      },
      {
        q: "What happens to staff when a business is sold?",
        a: "Employee entitlements are usually adjusted at settlement and the buyer decides who to offer employment to. It needs to be worked out in the contract, not on settlement day.",
      },
    ],
  },
  {
    slug: "kings-beach",
    name: "Kings Beach",
    title: "Kings Beach business sales, management rights and lease lawyers",
    metaTitle: `Kings Beach Business Sales & Management Rights Lawyers | ${FIRM.name}`,
    description: `Kings Beach lawyers for business sales, accommodation management rights and commercial leases. Fixed-fee contract review and settlement. Call ${phone}.`,
    lead: "Kings Beach is accommodation and hospitality country. The businesses that change hands here are cafes, surf and beach retail, short-stay letting operations and management rights over the towers along the beachfront.",
    paragraphs: [
      "Management rights sales are their own discipline. The value is the verified net profit of the caretaking and letting agreements, the term remaining on those agreements, and whether the body corporate will approve the incoming manager. Any of the three can sink a deal.",
      "Hospitality and retail sales here live and die on the lease and on trading figures that reflect a genuinely seasonal market.",
      "We act for buyers and sellers on both, and we say plainly when the numbers or the approvals do not support the price.",
    ],
    localNotes: [
      {
        t: "Body corporate approval",
        d: "An incoming management rights buyer must be approved by the body corporate. Interviews, training conditions and committee politics all sit on the critical path.",
      },
      {
        t: "Agreement term remaining",
        d: "Caretaking and letting agreements run for a fixed term. Short remaining terms cut the value and the financeability of the business — check before you offer.",
      },
      {
        t: "Verified net profit",
        d: "The profit figure should be verified by an accountant experienced in management rights, not taken from the seller's spreadsheet.",
      },
      {
        t: "Seasonal hospitality figures",
        d: "Kings Beach trade peaks in the holidays. Twelve months of figures is the minimum before you value goodwill.",
      },
    ],
    sectors: ["Management rights", "Cafes and restaurants", "Beach and surf retail", "Short-stay letting", "Personal services"],
    nearby: ["Caloundra", "Shelly Beach", "Moffat Beach", "Golden Beach", "Dicky Beach"],
    faqs: [
      {
        q: "Do you act on management rights purchases at Kings Beach?",
        a: "Yes. We review the caretaking and letting agreements, the body corporate records and the approval process, and coordinate with your accountant on the profit verification.",
      },
      {
        q: "How much of a management rights price is the unit?",
        a: "Usually a large part of it. The manager's unit is bought as real property alongside the business, so the deal is a business purchase and a conveyance running together.",
      },
      {
        q: "Can a body corporate refuse to approve me?",
        a: "It cannot unreasonably withhold approval, but it can impose reasonable conditions and it can take time. Your contract should be conditional on that approval.",
      },
    ],
  },
  {
    slug: "currimundi",
    name: "Currimundi",
    title: "Currimundi business sale and commercial lease lawyers",
    metaTitle: `Currimundi Business Sale & Commercial Lease Lawyers | ${FIRM.name}`,
    description: `Business lawyers for Currimundi and Aroona: buying or selling a business, lease assignment, due diligence and settlement on fixed fees. Call ${phone}.`,
    lead: "Currimundi trade is neighbourhood trade — the Currimundi Marketplace and local strips, trade businesses running out of home or a nearby shed, and allied health serving a settled residential catchment.",
    paragraphs: [
      "Neighbourhood businesses are often sold without a broker, on a handshake and a one-page heads of agreement. That is exactly where the trouble starts: no written restraint, no clear treatment of stock, no landlord consent and no agreement about staff.",
      "We turn those arrangements into a proper contract with a workable settlement, and for buyers we run the due diligence that a small deal still deserves.",
      "Fixed fees, quoted up front, and plain advice on whether the price makes sense.",
    ],
    localNotes: [
      {
        t: "Neighbourhood centre leases",
        d: "Anchor tenancy changes and centre redevelopment plans affect a small tenant's future trade. Ask what the landlord has planned before you buy the goodwill.",
      },
      {
        t: "Owner-operator trade businesses",
        d: "Where the goodwill is the owner, a restraint of trade and a handover period are the only things protecting the buyer. Both need to be drafted to be enforceable.",
      },
      {
        t: "Vehicles and financed plant",
        d: "Utes, trailers and equipment are frequently under finance. PPSR searches and payout letters before settlement, every time.",
      },
      {
        t: "Home-based operations",
        d: "If the business runs from home, check the council approval and whether the business is genuinely transferable to a different address.",
      },
    ],
    sectors: ["Trade and services", "Neighbourhood retail", "Allied health", "Food and takeaway", "Fitness"],
    nearby: ["Aroona", "Battery Hill", "Wurtulla", "Little Mountain", "Caloundra"],
    faqs: [
      {
        q: "Is a written contract necessary for a small business sale?",
        a: "Yes. Without one there is no restraint, no warranty about the figures, no agreed treatment of stock and staff, and no clear settlement date. It is the cheapest protection in the deal.",
      },
      {
        q: "What is a restraint of trade and will it hold up?",
        a: "It stops the seller competing nearby for a period. Queensland courts enforce restraints that are reasonable in area and duration, so a well-drafted cascading clause is far safer than a broad one.",
      },
      {
        q: "Do you act for sellers as well as buyers?",
        a: "Yes — and for sellers the work starts before going to market, assembling the lease, financials and equipment list so due diligence does not drag the price down.",
      },
    ],
  },
  {
    slug: "little-mountain",
    name: "Little Mountain",
    title: "Little Mountain and Caloundra West business lawyers",
    metaTitle: `Little Mountain Business & Commercial Lawyers | ${FIRM.name}`,
    description: `Little Mountain and Caloundra West business lawyers: business sales, industrial leases, equipment finance and settlement. Fixed fees. Call ${phone}.`,
    lead: "Little Mountain and the Caloundra West industrial pocket are where the Coast's trade, transport and light manufacturing businesses sit. Those deals look nothing like a cafe sale.",
    paragraphs: [
      "An industrial business sale usually involves a warehouse or yard lease, significant plant, several employees with accrued entitlements, contracts with commercial customers, and a PPSR register full of registrations that need releasing at settlement.",
      "Buyers should be looking at whether the customer contracts transfer, whether key staff will stay, what condition the plant is in and what the make-good obligation on the shed will cost at the end of the term.",
      "We run the contract, the searches, the lease assignment and the settlement mechanics, and we give you a fixed fee before we start.",
    ],
    localNotes: [
      {
        t: "Industrial leases and make-good",
        d: "Racking, hardstand and yard modifications can create a substantial make-good liability. Confirm what the outgoing tenant agreed to before you inherit it.",
      },
      {
        t: "Plant, equipment and PPSR",
        d: "Financed forklifts, vehicles and machinery need payout figures and releases timed to settlement, not chased afterwards.",
      },
      {
        t: "Customer contracts and assignment",
        d: "Commercial supply agreements often need the customer's consent to assign. Missing that can hand a buyer a business without its main revenue.",
      },
      {
        t: "Employees and entitlements",
        d: "Long-serving staff carry real leave balances. Who pays them, and how they are adjusted at settlement, must be written into the contract.",
      },
    ],
    sectors: ["Light industrial", "Transport and logistics", "Trade contracting", "Wholesale and distribution", "Automotive"],
    nearby: ["Caloundra", "Meridan Plains", "Aroona", "Currimundi", "Baringa"],
    faqs: [
      {
        q: "What searches do you run on a business purchase?",
        a: "PPSR against the seller and the equipment, ASIC and company searches, land and lease checks on the premises, licence and permit verification, and court and insolvency checks where the deal size warrants it.",
      },
      {
        q: "Is GST payable on a business sale?",
        a: "Often not — many sales qualify as a going concern and are GST-free, but only if the contract is drafted correctly and both parties are registered. Getting that wrong is an expensive mistake.",
      },
      {
        q: "Can you help before I go to market?",
        a: "Yes. Sellers who assemble the lease, financials, employee data and equipment list up front settle faster and defend their price better.",
      },
    ],
  },
  {
    slug: "baringa",
    name: "Baringa",
    title: "Baringa and Aura business, franchise and lease lawyers",
    metaTitle: `Baringa Business, Franchise & Lease Lawyers | ${FIRM.name}`,
    description: `Baringa and Aura business lawyers: new tenancies, franchise agreements, business purchases and commercial leases. Fixed-fee advice. Call ${phone}.`,
    lead: "Baringa and the wider Aura development are the newest commercial ground on the southern Sunshine Coast. Most of the work here is starting a business rather than buying one — new leases, franchise agreements and first fit-outs.",
    paragraphs: [
      "A new-estate tenancy is a long bet on a catchment that is still being built. The lease terms, the incentive package and the exit rights matter more than usual, because trade in year one rarely looks like trade in year five.",
      "Franchises are common in new centres. A franchise agreement plus a lease plus a disclosure document is three sets of obligations, and the franchisor's documents are not negotiable in the way a private sale is — but they are still worth understanding before you sign.",
      "We review leases and franchise packages, act on business purchases, and handle commercial conveyancing for owner-occupiers buying into the estate.",
    ],
    localNotes: [
      {
        t: "Pre-commitment leases",
        d: "Agreements to lease signed off the plan carry handover date risk. Build in what happens if the centre or the tenancy is delivered late.",
      },
      {
        t: "Franchise disclosure",
        d: "The Franchising Code sets disclosure and cooling-off requirements. Use the cooling-off period rather than relying on it never being needed.",
      },
      {
        t: "Incentives and fit-out",
        d: "Rent-free periods and fit-out contributions must be documented in the lease with clear conditions for repayment if you leave early.",
      },
      {
        t: "Growing catchment, thin history",
        d: "There is little trading history to value in a new estate. Be careful about paying goodwill on projections rather than results.",
      },
    ],
    sectors: ["Franchise retail and food", "Childcare and education", "Health and medical", "Trade services", "Professional suites"],
    nearby: ["Pelican Waters", "Little Mountain", "Caloundra", "Golden Beach", "Meridan Plains"],
    faqs: [
      {
        q: "Should I get a franchise agreement reviewed?",
        a: "Yes. Even where the terms are fixed, you should know what you are committing to on term, renewal, territory, fees, supply and what happens if you want to sell.",
      },
      {
        q: "Do you act on new leases in Aura?",
        a: "Yes — reviewing and negotiating the lease or agreement to lease, the incentive documentation and the guarantees the landlord asks directors to give.",
      },
      {
        q: "Can I buy the premises instead of leasing?",
        a: "In some parts of the estate, yes. We handle the commercial conveyance, including GST, the going-concern position and any existing tenancy.",
      },
    ],
  },
];

export function getBusinessLocation(slug: string): BusinessLocation | undefined {
  return BUSINESS_LOCATIONS.find((l) => l.slug === slug);
}
